import React from "react";
import { isCopilotAsyncChatEnabled } from "owa-capabilities-definitions/lib/copilotAsyncChatCapability";
import { mergeClasses, DrawerBody, DrawerHeader, DrawerHeaderTitle, InlineDrawer, Button, } from "@fluentui/react-components";
import { Dismiss20Regular, PanelRightContractRegular, PanelRightExpandRegular, } from "@fluentui/react-icons";
import type { PageService, HostedFilesService } from "owa-app-host-services";
import { observer } from "owa-mobx-react";
import { useComputedValue } from "owa-react-hooks/lib/useComputed";
import { useAction } from "owa-react-hooks/lib/useAction";
import { useResizeObserver } from "owa-react-hooks/lib/useResizeObserver";
import { MAIN_BUTTON_ID } from "owa-copilot-dab";
import { closeCopilotChatDrawer, isExpandCollapseButtonShown, isCopilotChatDrawerExpanded, toggleCopilotChatDrawerExpandCollapse, onCopilotChatDrawerReady, resizeCopilotChatDrawer, getCopilotChatDrawerTrueWidth, getShowBizChatInReadingPane, logCopilotChatDrawerStartingWidth, getBizChatWindowKey, getHeartbeatMemorySizesMb, isTasksHubActive, setTasksHubActiveMutator, MINIMUM_COPILOT_CHAT_DRAWER_WIDTH, getCopilotChatDrawerMaximumWidth, setAppCanvasWidthMutator, } from "owa-copilot-chat-drawer-store";
import { ResizeHandle, ResizeHandleScenario, ResizeHandleDirection } from "owa-resize-handle";
import { M365ChatContainer } from "owa-sidepane-bizchat";
import { getCopilotUpsellEligibility, LazyCopilotUpsellPane } from "owa-copilot-upsell";
import { getCopilotChatDrawerStyles } from "./getCopilotChatDrawerStyles";
import { CopilotAccountSwitcher } from "owa-copilot-account-picker-dialog";
import { getCurrentAccount as getCurrentAccountFromPicker, resetCopilotAccountPickerDialogStoreMutator, } from "owa-copilot-account-picker-store";
import { container, denselessMargin, drawerBody, drawerHeader, headerTitle, copilotIconAndTitle, drawerContainer, ssrBackground, ssrDarkBackground, } from "./CopilotChatDrawer.scss";
import { customScrollBarOnHover } from "owa-global-styles";
import { closeCopilotChatDrawer as CopilotChatDrawerStrings_closeCopilotChatDrawer, collapseCopilotChatDrawer as CopilotChatDrawerStrings_collapseCopilotChatDrawer, expandCopilotChatDrawer as CopilotChatDrawerStrings_expandCopilotChatDrawer, } from "../localization/CopilotChatDrawer.locstring.json";
import loc, { isCurrentCultureRightToLeft } from "owa-localize";
import { isGlobalFeatureEnabled, isAccountFeatureEnabled } from "owa-feature-flags";
import { isOutlookAgentSdxActive, OutlookAgentSdxPane } from "officeaicopilot-agent-outlook";
import { trace } from "owa-trace";
import type { IContextualChat } from "owa-bizchat-types/lib/types";
import { getIndexerValueForMailboxInfo } from "owa-client-types";
import type { MailboxInfo } from "owa-client-types";
import { useRegisterNavigationLoopContainer } from "owa-accessibility-loop-nav/lib/hooks/useRegisterNavigationLoopContainer";
import { LazyTasksHub, logTasksHubClosed, type TasksHubExternalSection, } from "owa-copilot-chat-tasks-hub";
import { openAsyncConversation } from "owa-copilot-async-chat-utils/lib/utils/openAsyncConversation";
import { invalidatePostMessageHandler } from "owa-bizchat-shared/lib/utils/PostMessageToFrameHandler";
import isPopout from "owa-popout-v2/lib/utils/isPopout";
import { AgentJobsWorkflowsBody, AgentJobsAutomationsBody, } from "owa-calendar-ai-labs-agentjobs-panel";
// Deep import: the section IDs + localized header getters live in an
// import-free-ish leaf (it pulls owa-localize + a small locstring). Importing
// them via the dedicated subpath keeps this localization machinery out of the
// eager calendar-surface bundles that consume the package root for other symbols.
import { AGENT_JOBS_AUTOMATIONS_SECTION_ID, AGENT_JOBS_WORKFLOWS_SECTION_ID, getAgentJobsAutomationsHeader, getAgentJobsWorkflowsHeader, } from "owa-calendar-ai-labs-agentjobs-panel/lib/embeddedSectionIds";
import { getCurrentModule } from "owa-app-module-store";
import { Module } from "owa-workloads-module";
import { isBizchatSidepaneSSREnabled } from "owa-sidepane-bizchat/lib/utils/isBizchatSidepaneSSREnabled";
import { getIsDarkTheme } from "owa-fabric-theme";
import { LazyPaneSwitcher } from "./LazyPaneSwitcher";

const dismissIcon = <Dismiss20Regular />;
const expandIcon = <PanelRightExpandRegular />;
const collapseIcon = <PanelRightContractRegular />;

export type CopilotChatDrawerProps = {
    mailboxInfo: MailboxInfo;
    targetWindow: Window; // The host window of current CopilotChat instance
    isVisible: boolean;
    dismissOverride?: () => void; // If provided, this function will be called when the user clicks the dismiss button
    hideExpandCollapseButton?: boolean; // If true, hide the expand/collapse button
    elementStyles?: Partial<Record<"drawerContainer" | "drawerHeader" | "inlineDrawer", React.CSSProperties>>; // If provided, these styles will be applied to the corresponding elements
    getChatContext?: () => Promise<IContextualChat>; // Function to get the chat context annotation
    overrideScenario?: string; // If provided, this scenario will be passed to the M365ChatContainer to override the default scenario
    getPageService?: (appId: string) => PageService; // Function to get the page service. Used when hosted inside Teams where we need to create the PageService differently(HostedPageService).
    getFilesService?: () => HostedFilesService; // If provided, this factory will be called to get the service used to proxy file operations to the root hub. Used when hosted inside Teams Calendar.
    rootHub?: string; // Used in Teams to specify the root hub for the chat. In Teams Calendar, bizchat is hosted as a nested app (Teams -> Calendar -> Bizchat). We need to pass in the root hub(Teams) to ensure correct behavior in scenarios that need this info, like getting customized Zero Query.
    isNAAChannelRecommended?: boolean; // If true, Nested App Auth channel will be recommended for BizChat
    topOrigin?: string;
    isDeeplyNestedAppAuthSupported?: boolean;
    addNAATrustedOrigins?: (appOrigins: string[]) => Promise<string>;
    disableAutoFocus?: boolean;
    observeParentCanvas?: boolean;
    appCanvasRef?: React.RefObject<HTMLDivElement | null>;
};

export const CopilotChatDrawer: React.FunctionComponent<CopilotChatDrawerProps> = observer(function CopilotChatDrawer({ mailboxInfo, targetWindow, isVisible, dismissOverride, hideExpandCollapseButton, elementStyles, getChatContext, overrideScenario, getPageService, getFilesService, rootHub, isNAAChannelRecommended, topOrigin, isDeeplyNestedAppAuthSupported, addNAATrustedOrigins, disableAutoFocus, observeParentCanvas, appCanvasRef, }) {
    const { copilotPane, overrideFuiIconFont } = getCopilotChatDrawerStyles();
    
    const showExpandCollapseButton = hideExpandCollapseButton
        ? false
        : isExpandCollapseButtonShown(targetWindow);
    const isCopilotChatDrawerInExpandedState = isCopilotChatDrawerExpanded(targetWindow);
    const isRTL = isCurrentCultureRightToLeft();
    const isDenselessEnabled = isGlobalFeatureEnabled("rp-denseless");
    const isAccountSwitcherEnabled = isGlobalFeatureEnabled("acct-copilot-bizchat-account-switcher");
    const isDevMailboxInfoEnabled = isGlobalFeatureEnabled("mon-copilot-dev-mailboxInfo");
    
    const drawerRef = React.useRef<HTMLDivElement | null>(null);
    const parentCanvasRef = React.useRef<HTMLDivElement | null>(null);
    const setDrawerRef = React.useCallback((drawer: HTMLDivElement | null) => {
        drawerRef.current = drawer;
        parentCanvasRef.current = observeParentCanvas
            ? (drawer?.parentElement as HTMLDivElement | null)
            : null;
    }, [observeParentCanvas]);
    const appCanvasToObserve = appCanvasRef ?? parentCanvasRef;
    const updateAppCanvasWidth = React.useCallback((bounds: DOMRectReadOnly) => {
        setAppCanvasWidthMutator(targetWindow, bounds.width);
    }, [targetWindow]);
    useResizeObserver("CopilotChatDrawerCanvas", appCanvasToObserve, updateAppCanvasWidth);
    useRegisterNavigationLoopContainer("CopilotChat", drawerRef);
    const showInReadingPane = getShowBizChatInReadingPane(targetWindow);
    
    const selectedMailboxInfo: MailboxInfo = useComputedValue(() => {
        return isAccountSwitcherEnabled
            ? (getCurrentAccountFromPicker() ?? mailboxInfo)
            : mailboxInfo;
    }, [isAccountSwitcherEnabled, mailboxInfo]);
    
    const isDevHighMemoryEnabled = isGlobalFeatureEnabled("mon-copilot-dev-highMemory") &&
        isAccountFeatureEnabled("mon-copilot-bizchat-ocdi", selectedMailboxInfo);
    
    const isSSREnabled = isBizchatSidepaneSSREnabled(selectedMailboxInfo);
    const isDarkTheme = getIsDarkTheme();
    
    const { workingSetSizeMb, commitSizeMb, isProcessShared } = getHeartbeatMemorySizesMb();
    
    useAction(onCopilotChatDrawerReady);
    
    const logStartingWidth = React.useCallback(() => logCopilotChatDrawerStartingWidth(targetWindow, selectedMailboxInfo), [targetWindow, selectedMailboxInfo]);
    useAction(logStartingWidth);
    
    const handleExpandCollapseClick = React.useCallback(() => {
        toggleCopilotChatDrawerExpandCollapse(targetWindow, !isCopilotChatDrawerInExpandedState, selectedMailboxInfo);
    }, [targetWindow, isCopilotChatDrawerInExpandedState, selectedMailboxInfo]);
    
    const handleCloseClick = React.useCallback(() => {
        if (isAccountSwitcherEnabled) {
            resetCopilotAccountPickerDialogStoreMutator();
        }
        closeCopilotChatDrawer(targetWindow, selectedMailboxInfo);
    }, [targetWindow, selectedMailboxInfo, isAccountSwitcherEnabled]);
    
    const handleCopilotChatResized = React.useCallback((copilotChatDrawerWidth: number) => {
        resizeCopilotChatDrawer(targetWindow, copilotChatDrawerWidth, selectedMailboxInfo);
    }, [targetWindow, selectedMailboxInfo]);
    
    const windowKey = getBizChatWindowKey(targetWindow);
    
    // Open a Tasks Hub conversation in this window's BizChat sidepane. Injected from the drawer
    // because the open needs window context the drawer owns (targetWindow, the view-flip, and
    // this window's post-message handler), keeping Tasks Hub decoupled from how chats open.
    const handleOpenTasksHubConversation = React.useCallback((conversationId: string, conversationMailboxInfo: MailboxInfo) => {
        logTasksHubClosed(windowKey, conversationMailboxInfo, "openedConversation", conversationId);
        
        // Flip back to the chat view; the M365 iframe is unmounted while Tasks Hub is shown.
        setTasksHubActiveMutator(windowKey, false);
        
        // Clear the stale handler pointing at the just-unmounted iframe so openBizChatSidePane
        // takes the cold path instead of posting into the dying frame (which boots empty).
        invalidatePostMessageHandler(conversationMailboxInfo, targetWindow);
        
        // Marks read + loads the conversation into the sidepane (shared with the DAB stack).
        openAsyncConversation({
            conversationId,
            mailboxInfo: conversationMailboxInfo,
            targetWindow,
            source: "CopilotTasksHub",
        });
    }, [targetWindow, windowKey]);
    
    const isMainSurface = !isPopout(targetWindow);
    const isMainMailSurface = isMainSurface && getCurrentModule() === Module.Mail;
    const isMainCalendarSurface = isMainSurface && getCurrentModule() === Module.Calendar;
    
    // Tasks Hub is available on the main Mail surface behind its flight, and on the
    // main Calendar surface only when the agent-jobs panel flight is also on.
    const isAsyncChatEnabled = isCopilotAsyncChatEnabled(selectedMailboxInfo);
    const isTasksHubEnabled = (isMainMailSurface && isAsyncChatEnabled) ||
        (isMainCalendarSurface &&
            isAsyncChatEnabled &&
            isGlobalFeatureEnabled("cal-agent-jobs-panel"));
    const isTasksHubVisible = isTasksHubEnabled && isTasksHubActive(windowKey);
    const isM365ChatVisible = isVisible && !isTasksHubVisible;
    
    // Render a Copilot promo in place of real chat for free/basic MSA users
    const showCopilotUpsell = isMainSurface && getCopilotUpsellEligibility(selectedMailboxInfo);
    
    // Agent-jobs sections embed inside the Tasks Hub and require both
    // `copilot-async-chat` and `cal-agent-jobs-panel`.
    // - Calendar gets the "Automations" and "Workflows" sections
    // - Mail gets only the "Automations" section.
    const showAgentJobsCalendar = isMainCalendarSurface &&
        isAsyncChatEnabled &&
        isGlobalFeatureEnabled("cal-agent-jobs-panel");
    
    const showAgentJobsMail = isMainMailSurface &&
        isAsyncChatEnabled &&
        isGlobalFeatureEnabled("cal-agent-jobs-panel");
    
    const showAgentJobs = showAgentJobsCalendar || showAgentJobsMail;
    
    // Build the Tasks Hub external sections. Tasks Hub owns the section
    // chrome (AccordionItem + header + badge + divider) and renders each
    // descriptor's opaque body, so the agent-jobs sections can never drift
    // from the chat sections. The section bodies stay lazy-loaded (zero cost
    // off). The primary body owns the shared "panel viewed" telemetry +
    // dialogs; Workflows is primary on Calendar, otherwise Automations is.
    // Values are owned by the calendar package so this host doesn't hardcode
    // the section identifiers/headers, and Workflows opens by default on
    // Calendar.
    const agentJobsExternalSections = React.useMemo<TasksHubExternalSection[] | undefined>(() => {
        if (!showAgentJobs) {
            return undefined;
        }
        const sections: TasksHubExternalSection[] = [];
        // Remount the bodies when the selected account changes so no
        // previous-account UI state (active output, expanded rows) leaks
        // across an account switch and the per-mailbox data load re-fires for
        // the new account. NOTE: the agent-jobs stores are still global (not
        // mailbox-partitioned), so a stale previous-account list can flash for
        // the duration of the new load — full isolation is tracked as a
        // follow-up (mailbox-key the calendar-agent stores).
        const mailboxKey = getIndexerValueForMailboxInfo(selectedMailboxInfo);
        if (showAgentJobsCalendar) {
            sections.push({
                id: AGENT_JOBS_WORKFLOWS_SECTION_ID,
                header: getAgentJobsWorkflowsHeader(),
                body: (<AgentJobsWorkflowsBody key={mailboxKey} mailboxInfo={selectedMailboxInfo} primary={true}/>),
                defaultOpen: true,
            });
        }
        sections.push({
            id: AGENT_JOBS_AUTOMATIONS_SECTION_ID,
            header: getAgentJobsAutomationsHeader(),
            // Automations is primary only when there's no Workflows section.
            body: (<AgentJobsAutomationsBody key={mailboxKey} mailboxInfo={selectedMailboxInfo} primary={!showAgentJobsCalendar}/>),
        });
        return sections;
    }, [showAgentJobs, showAgentJobsCalendar, selectedMailboxInfo]);
    
    const drawerHeaderActions = React.useMemo(() => {
        const expandCollapseButtonLabel = isCopilotChatDrawerInExpandedState
            ? loc(CopilotChatDrawerStrings_collapseCopilotChatDrawer)
            : loc(CopilotChatDrawerStrings_expandCopilotChatDrawer);
        
        const expandCollapseButtonIcon = isCopilotChatDrawerInExpandedState
            ? isRTL
                ? expandIcon
                : collapseIcon
            : isRTL
                ? collapseIcon
                : expandIcon;
        
        const closeButtonLabel = loc(CopilotChatDrawerStrings_closeCopilotChatDrawer);
        
        return (<>
                    {showExpandCollapseButton && (<Button appearance="transparent" aria-label={expandCollapseButtonLabel} title={expandCollapseButtonLabel} onClick={handleExpandCollapseClick} icon={expandCollapseButtonIcon} onKeyDown={onExpandCollapseKeyDown}/>)}
                    <Button appearance="transparent" aria-label={closeButtonLabel} title={closeButtonLabel} icon={dismissIcon} onClick={dismissOverride ?? handleCloseClick}/>
                </>);
    }, [
        isCopilotChatDrawerInExpandedState,
        isRTL,
        showExpandCollapseButton,
        handleExpandCollapseClick,
        handleCloseClick,
        dismissOverride,
    ]);
    
    // Compute the style to make width of the drawer component
    const copilotChatDrawerWidth = getCopilotChatDrawerTrueWidth(targetWindow);
    const copilotChatDrawerMaximumWidth = getCopilotChatDrawerMaximumWidth(targetWindow);
    const controlledResize = React.useMemo(() => ({
        dimension: {
            value: copilotChatDrawerWidth,
            min: Math.min(MINIMUM_COPILOT_CHAT_DRAWER_WIDTH, copilotChatDrawerWidth),
            max: Math.max(copilotChatDrawerWidth, copilotChatDrawerMaximumWidth),
            edge: "start" as const,
        },
        onResize: handleCopilotChatResized,
    }), [copilotChatDrawerMaximumWidth, copilotChatDrawerWidth, handleCopilotChatResized]);
    const drawerContainerStyle = useComputedValue(() => {
        trace.info(` Copilot chat drawer view state: isVisible: ${isVisible}`, "iframeWrapper");
        if (showInReadingPane) {
            return {
                display: isVisible ? "flex" : "none",
                flexGrow: 1,
                ...(elementStyles?.drawerContainer ?? {}),
            };
        }
        return {
            width: `${copilotChatDrawerWidth}px`,
            display: isVisible ? "flex" : "none",
            ...(elementStyles?.drawerContainer ?? {}),
        };
    }, [copilotChatDrawerWidth, isVisible, elementStyles?.drawerContainer, showInReadingPane]);
    
    const drawerHeaderClasses = mergeClasses(drawerHeader, overrideFuiIconFont);
    
    const isCachingEnabled = (!targetWindow || targetWindow === window) &&
        isGlobalFeatureEnabled("mon-copilot-bizchat-caching");
    
    return (<>
                {isVisible && !showInReadingPane && (<ResizeHandle direction={ResizeHandleDirection.vertical} controlledResize={controlledResize} scenario={ResizeHandleScenario.CopilotChatDrawer} data-resize-scenario={"CopilotChatDrawer"}/>)}
                <div data-app-section={"CopilotChat"} ref={setDrawerRef} className={mergeClasses(container, isDenselessEnabled && denselessMargin, isSSREnabled && ssrBackground, isSSREnabled && isDarkTheme && ssrDarkBackground)} style={drawerContainerStyle}>
                    <InlineDrawer position="end" open={true} className={drawerContainer} style={elementStyles?.inlineDrawer}>
                        {/* The Outlook Copilot SDX owns its own chrome, so OWA's drawer header is
         * suppressed while it is hosting the pane. */}
                        {!isOutlookAgentSdxActive(selectedMailboxInfo) && (<DrawerHeader className={drawerHeaderClasses}>
                                <DrawerHeaderTitle action={drawerHeaderActions}>
                                    <div className={copilotIconAndTitle}>
                                        {isTasksHubEnabled ? (<LazyPaneSwitcher mailboxInfo={selectedMailboxInfo} targetWindow={targetWindow}/>) : (<span className={headerTitle}>Copilot</span>)}
                                    </div>
                                </DrawerHeaderTitle>
                            </DrawerHeader>)}

                        <DrawerBody className={mergeClasses(drawerBody, customScrollBarOnHover)}>
                            <div className={copilotPane}>
                                {isDevMailboxInfoEnabled && (<div style={devMailboxInfoBannerStyle}>
                                        <div>[DEV] selectedMailboxInfo</div>
                                        <div>type: {selectedMailboxInfo.type}</div>
                                        <div>identity: {selectedMailboxInfo.userIdentity}</div>
                                        <div>smtp: {selectedMailboxInfo.mailboxSmtpAddress}</div>
                                        {selectedMailboxInfo.mailboxRank && (<div>rank: {selectedMailboxInfo.mailboxRank}</div>)}
                                    </div>)}
                                {isDevHighMemoryEnabled &&
            renderDevHighMemoryBanner(workingSetSizeMb, commitSizeMb, isProcessShared)}
                                {isAccountSwitcherEnabled && !isTasksHubVisible && (<CopilotAccountSwitcher currentContextMailboxInfo={selectedMailboxInfo} isDrawerVisible={isVisible} targetWindow={targetWindow}/>)}

                                {isTasksHubVisible && (<LazyTasksHub mailboxInfo={selectedMailboxInfo} externalSections={agentJobsExternalSections} onOpenConversation={handleOpenTasksHubConversation}/>)}

                                {showCopilotUpsell ? (<LazyCopilotUpsellPane key={getIndexerValueForMailboxInfo(selectedMailboxInfo)} mailboxInfo={selectedMailboxInfo}/>) : isOutlookAgentSdxActive(selectedMailboxInfo) ? (<OutlookAgentSdxPane key={getIndexerValueForMailboxInfo(selectedMailboxInfo)} mailboxInfo={selectedMailboxInfo} isVisible={isM365ChatVisible} targetWindow={targetWindow}/>) : (<M365ChatContainer mailboxInfo={selectedMailboxInfo} isVisible={isM365ChatVisible} targetWindow={targetWindow} getChatContext={getChatContext} overrideScenario={overrideScenario} isCachingEnabled={isCachingEnabled} getPageService={getPageService} getFilesService={getFilesService} rootHub={rootHub} isNAAChannelRecommended={isNAAChannelRecommended} topOrigin={topOrigin} isDeeplyNestedAppAuthSupported={isDeeplyNestedAppAuthSupported} addNAATrustedOrigins={addNAATrustedOrigins} disableAutoFocus={disableAutoFocus}/>)}
                            </div>
                        </DrawerBody>
                    </InlineDrawer>
                </div>
            </>);
}, "CopilotChatDrawer");

// Accessibility implement to ensure shift+tab on expand/collapse properly
// goes back to the Copilot Command Center split action primary button.
const onExpandCollapseKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Tab" && e.shiftKey) {
        e.preventDefault();
        const target = document.getElementById(MAIN_BUTTON_ID);
        target?.focus();
    }
};

// This style is only used when dev flight is enabled to display the selected mailbox info for debugging purpose.
const devMailboxInfoBannerStyle: React.CSSProperties = {
    padding: "4px 8px",
    background: "#1a1a2e",
    color: "#00ff88",
    fontFamily: "monospace",
    fontSize: "11px",
    borderBottom: "1px solid #333",
    userSelect: "text",
};

// This style is only used when dev flight is enabled to display high memory warning.
const devHighMemoryBannerStyle: React.CSSProperties = {
    padding: "4px 8px",
    background: "#2e1a1a",
    color: "#ff4444",
    fontFamily: "monospace",
    fontSize: "11px",
    borderBottom: "1px solid #333",
    userSelect: "text",
};

function renderDevHighMemoryBanner(workingSetSizeMb: number | undefined, commitSizeMb: number | undefined, isProcessShared: boolean | undefined): React.ReactNode {
    const workingSetThreshold = isProcessShared ? 560 : 410;
    const commitSizeThreshold = isProcessShared ? 570 : 375;
    
    const isOverThreshold = (workingSetSizeMb !== undefined && workingSetSizeMb > workingSetThreshold) ||
        (commitSizeMb !== undefined && commitSizeMb > commitSizeThreshold);
    
    if (!isOverThreshold) {
        return null;
    }
    
    return (<div style={devHighMemoryBannerStyle}>
            <div>⚠️ [DEV] High Memory Detected</div>
            <div>Working Set: {workingSetSizeMb ?? "?"} MB</div>
            <div>Commit Size: {commitSizeMb ?? "?"} MB</div>
            <div>
                Process Shared: {isProcessShared === undefined ? "?" : String(isProcessShared)}
            </div>
        </div>);
}
